import React, { useMemo, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { media, getAllPortfolio } from "@/lib/media";

export default function Portfolio() {
  const { t } = useLanguage();
  const [active, setActive] = useState("all");

  const categories = [
    { key: "all", label: t("portfolio.tabs.all") },
    { key: "weddings", label: t("portfolio.tabs.weddings") },
    { key: "family", label: t("portfolio.tabs.family") },
    { key: "fashion", label: t("portfolio.tabs.fashion") },
    { key: "christmas", label: t("portfolio.tabs.christmas") },
  ];

  const items = useMemo(() => {
    if (active === "all") return getAllPortfolio();
    return (media.portfolio[active] || []).map((x) => ({ ...x, category: active }));
  }, [active]);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-sand py-24 sm:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <p className="overline mb-4" data-testid="portfolio-overline">
            {t("portfolio.overline")}
          </p>
          <h2
            data-testid="portfolio-title"
            className="font-display text-4xl leading-tight text-ocean sm:text-5xl lg:text-6xl"
          >
            {t("portfolio.title")}
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-mutedText">
            {t("portfolio.subtitle")}
          </p>
        </div>

        {/* Filters — minimalist floating text tabs, centered */}
        <div
          data-testid="portfolio-tabs"
          className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-y border-warmBorder/70 py-4"
        >
          {categories.map((c) => {
            const isActive = active === c.key;
            return (
              <button
                key={c.key}
                type="button"
                data-testid={`portfolio-tab-${c.key}`}
                onClick={() => setActive(c.key)}
                className={`text-[11px] uppercase tracking-[0.28em] transition-colors duration-200 ${
                  isActive
                    ? "text-terracotta"
                    : "text-ocean/60 hover:text-ocean"
                }`}
              >
                {c.label}
                <span
                  className={`ml-2 inline-block h-px w-6 align-middle transition-colors duration-200 ${
                    isActive ? "bg-terracotta" : "bg-transparent"
                  }`}
                  aria-hidden
                />
              </button>
            );
          })}
        </div>

        {/* Uniform gallery grid — equal-size tiles */}
        <div
          key={active}
          data-testid="portfolio-grid"
          className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {items.map((item, idx) => (
            <figure
              key={`${item.src}-${idx}`}
              className="group relative overflow-hidden rounded-sm bg-sandSurface animate-fade-up aspect-[4/5]"
              style={{ animationDelay: `${Math.min(idx, 8) * 70}ms` }}
              data-testid={`portfolio-item-${idx}`}
            >
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ocean/55 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <figcaption className="pointer-events-none absolute bottom-4 left-5 right-5 translate-y-3 text-sand opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                <span className="text-[10px] uppercase tracking-[0.3em] text-terracotta">
                  {t(`portfolio.tabs.${item.category}`)}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
